import React, {ReactElement, useEffect, useState} from "react";
import {ThemeProvider} from "styled-components";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {Button} from "@zendeskgarden/react-buttons";
import {Separator} from "@zendeskgarden/react-dropdowns";
import {Inline} from "@zendeskgarden/react-loaders";
import {toast} from "react-toastify";
import {ViziblyTheme} from "../../../styles/zendesk-garden/ViziblyZDGTheme";
import {COLORS} from "../../../constants/Colors";
import {
    ManualPropertyFileArg,
    useGenerateManualPropertyFileUploadUrlsMutation,
    useProcessManualPropertyFileUploadsMutation
} from "../../../__generated__/generated_types";
import {
    ManualPropertyActualsButton,
    ManualPropertyActualsDataItem
} from "./components/csvbox/ManualPropertyActualsButton";
import {
    ManualPropertyRentRollButton,
    ManualPropertyRentRollDataItem
} from "./components/csvbox/ManualPropertyRentRollButton";
import {ManualPropertyBudgetButton, ManualPropertyBudgetDataItem} from "./components/csvbox/ManualPropertyBudgetButton";

export interface IFileUploadModal {
    propertyId: string;
    formVisible: boolean;
    jobCreated: (jobId: string) => void;
    onModalClose: () => void;
}

export function ManualPropertyFileUploadModal(props: IFileUploadModal): ReactElement {
    const {propertyId, formVisible, onModalClose} = props;

    const [accountValuesData, setAccountValuesData] = useState<ManualPropertyActualsDataItem[] | undefined>(undefined);
    const [accountValuesFilename, setAccountValuesFilename] = useState<string | undefined>(undefined);

    const [rentRollData, setRentRollData] = useState<ManualPropertyRentRollDataItem[] | undefined>(undefined);
    const [rentRollFilename, setRentRollFilename] = useState<string | undefined>(undefined);

    const [budgetValuesData, setBudgetValuesData] = useState<ManualPropertyBudgetDataItem[] | undefined>(undefined);
    const [budgetValuesFilename, setBudgetValuesFilename] = useState<string | undefined>(undefined);

    const [isUploading, setIsUploading] = useState<boolean>(false);

    const [generateUrls, {loading: loadingUrls, data: urlsData}] = useGenerateManualPropertyFileUploadUrlsMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [processFiles, {loading: loadingProcess}] = useProcessManualPropertyFileUploadsMutation({
        notifyOnNetworkStatusChange: true
    });

    useEffect(() => {
        if (urlsData && !loadingUrls && isUploading) {
            uploadFiles();
        }
    }, [urlsData, loadingUrls]);


    const reset = () => {
        setAccountValuesData(undefined);
        setAccountValuesFilename(undefined);

        setRentRollData(undefined);
        setRentRollFilename(undefined);

        setBudgetValuesData(undefined);
        setBudgetValuesFilename(undefined);

        setIsUploading(false);
        onModalClose();
    };

    const uploadFiles = () => {
        if(!urlsData || !accountValuesData || !rentRollData) {
            setIsUploading(false);
            return;
        }

        // Get URLs and keys
        const accountValuesUrl = urlsData.generateManualPropertyFileUploadUrls.accountValues.signedUrl;
        const accountValuesKey = urlsData.generateManualPropertyFileUploadUrls.accountValues.key;
        const rentRollUrl = urlsData.generateManualPropertyFileUploadUrls.rentRoll.signedUrl;
        const rentRollKey = urlsData.generateManualPropertyFileUploadUrls.rentRoll.key;

        const encoder = new TextEncoder();
        const accountValuesFile = encoder.encode(JSON.stringify(accountValuesData));
        const rentRollFile = encoder.encode(JSON.stringify(rentRollData));

        // Create array of upload promises
        const uploadPromises = [
            fetch(accountValuesUrl, {
                method: 'PUT',
                body: accountValuesFile.buffer,
                headers: {
                    'Content-Type': 'application/json',
                }
            }),
            fetch(rentRollUrl, {
                method: 'PUT',
                body: rentRollFile.buffer,
                headers: {
                    'Content-Type': 'application/json',
                }
            })
        ];

        // Add budget values file upload if it exists
        let budgetValuesKey: string | undefined;
        let budgetValuesUrl: string | undefined;

        if (budgetValuesData && urlsData.generateManualPropertyFileUploadUrls.budgetValues) {
            budgetValuesUrl = urlsData.generateManualPropertyFileUploadUrls.budgetValues.signedUrl;
            budgetValuesKey = urlsData.generateManualPropertyFileUploadUrls.budgetValues.key;

            const budgetValuesFile = encoder.encode(JSON.stringify(budgetValuesData));

            uploadPromises.push(
                fetch(budgetValuesUrl as string, {
                    method: 'PUT',
                    body: budgetValuesFile.buffer,
                    headers: {
                        'Content-Type': 'application/json',
                    }
                })
            );
        }

        Promise.all(uploadPromises)
            .then(() => {
                const accountValues: ManualPropertyFileArg = {
                    key: accountValuesKey,
                    signedUrl: accountValuesUrl
                };

                const rentRoll: ManualPropertyFileArg = {
                    key: rentRollKey,
                    signedUrl: rentRollUrl
                };

                let budgetValues: ManualPropertyFileArg | undefined;
                if (budgetValuesData && budgetValuesKey && budgetValuesUrl) {
                    budgetValues = {
                        key: budgetValuesKey,
                        signedUrl: budgetValuesUrl
                    };
                }

                return processFiles({
                    variables: {
                        propertyId,
                        accountValues,
                        rentRoll,
                        budgetValues
                    }
                });
            })
            .then(res => {
                if(res.data?.processManualPropertyFileUploads) {
                    props.jobCreated(res.data.processManualPropertyFileUploads.id);
                    toast.success("Files uploaded successfully");
                    reset();
                }
            })
            .catch((error) => {
                console.error("Error uploading files:", error);
                toast.error("Error uploading files");
            })
            .finally(() => {
                setIsUploading(false);
            });
    };

    const handleSubmit = () => {
        if (!accountValuesData || !rentRollData) {
            toast.error("Please select both account values and rent roll files");
            return;
        }

        setIsUploading(true);
        generateUrls({
            variables: {
                propertyId
            }
        });
    };

    if (!formVisible) {
        return <></>;
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Modal onClose={reset}>
                <Header>Upload Property Files</Header>
                <Body>
                    <form>
                       <div>
                           <h5 style={{fontWeight: "bold"}}>Account Values File (Required)</h5>
                           <ManualPropertyActualsButton
                               dataUploaded={(data, filename) => {
                                   setAccountValuesData(data);
                                   setAccountValuesFilename(filename);
                               }}
                               disabled={isUploading || accountValuesData !== undefined}
                           />
                           {accountValuesData && accountValuesFilename &&
                               <div style={{fontSize: "0.9rem", marginTop: "0.5rem"}}>
                                   {accountValuesFilename}
                                   <Button isBasic onClick={() => {
                                       setAccountValuesFilename(undefined);
                                       setAccountValuesData(undefined);
                                   }}>Remove</Button>
                               </div>
                           }
                       </div>
                        <div style={{marginTop: "1rem"}}>
                            <h5 style={{fontWeight: "bold"}}>Rent Roll File (Required)</h5>
                            <ManualPropertyRentRollButton
                                dataUploaded={(data, filename) => {
                                    setRentRollData(data);
                                    setRentRollFilename(filename);
                                }}
                                disabled={isUploading || rentRollData !== undefined}
                            />
                            {rentRollData && rentRollFilename &&
                                <div style={{fontSize: "0.9rem", marginTop: "0.5rem"}}>
                                    {rentRollFilename}
                                    <Button isBasic onClick={() => {
                                        setRentRollData(undefined);
                                        setRentRollFilename(undefined);
                                    }}>Remove</Button>
                                </div>
                            }
                        </div>
                        <div>
                            <h5 style={{fontWeight: "bold", marginTop: "1rem"}}>Budget Values File (Optional)</h5>
                            <ManualPropertyBudgetButton
                                dataUploaded={(data, filename) => {
                                    setBudgetValuesData(data);
                                    setBudgetValuesFilename(filename);
                                }}
                                disabled={isUploading || budgetValuesData !== undefined}
                            />
                            {budgetValuesData && budgetValuesFilename &&
                                <div style={{fontSize: "0.9rem", marginTop: "0.5rem"}}>
                                    {budgetValuesFilename}
                                    <Button isBasic onClick={() => {
                                        setBudgetValuesData(undefined);
                                        setBudgetValuesFilename(undefined);
                                    }}>Remove</Button>
                                </div>
                            }
                        </div>
                    </form>
                </Body>
                <Separator />
                <Footer>
                    <FooterItem>
                        <Button onClick={reset} isBasic disabled={isUploading || loadingUrls || loadingProcess}>
                            Cancel
                        </Button>
                    </FooterItem>
                    <FooterItem>
                        <Button isPrimary onClick={handleSubmit} disabled={!accountValuesData || !rentRollData || isUploading || loadingUrls || loadingProcess}>
                            {
                                (isUploading || loadingUrls || loadingProcess)
                                    ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading"/>
                                    : <span>Upload</span>
                            }
                        </Button>
                    </FooterItem>
                </Footer>
                <Close aria-label="Close modal" />
            </Modal>
        </ThemeProvider>
    );
}